import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { MessageCircle, Send, LoaderPinwheel, FileText } from "lucide-react";
import { streamRagChat } from "../../utils/ragChatStream";

const ChatPanel = ({ equipoId }) => {
  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const [expandido, setExpandido] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [pregunta, setPregunta] = useState("");
  const [enviando, setEnviando] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!expandido || !equipoId) return;

    const cargarHistorial = async () => {
      try {
        const res = await fetch(`${API}/ia/rag/chat/${equipoId}/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          console.error("No se pudo cargar el historial de chat:", res.status);
          return;
        }
        const data = await res.json();
        if (data.session) setSessionId(data.session._id);
        if (data.messages?.length) {
          setMensajes(
            data.messages.map((m) => ({
              role: m.role,
              content: m.content,
              citations: m.citations || [],
            })),
          );
        }
      } catch (err) {
        console.error("Error cargando historial de chat:", err);
      }
    };

    cargarHistorial();
  }, [expandido, equipoId, API, token]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mensajes]);

  const enviarPregunta = async () => {
    const texto = pregunta.trim();
    if (!texto || enviando) return;

    setPregunta("");
    setMensajes((prev) => [
      ...prev,
      { role: "user", content: texto },
      { role: "assistant", content: "", citations: [] },
    ]);
    setEnviando(true);

    try {
      await streamRagChat({
        apiUrl: API,
        token,
        equipoId,
        message: texto,
        sessionId,
        onSessionId: (id) => setSessionId(id),
        onDelta: (delta) => {
          setMensajes((prev) => {
            const copia = [...prev];
            copia[copia.length - 1] = {
              ...copia[copia.length - 1],
              content: copia[copia.length - 1].content + delta,
            };
            return copia;
          });
        },
        onDone: (citations) => {
          setMensajes((prev) => {
            const copia = [...prev];
            copia[copia.length - 1] = { ...copia[copia.length - 1], citations };
            return copia;
          });
        },
      });
    } catch (err) {
      setMensajes((prev) => {
        const copia = [...prev];
        copia[copia.length - 1] = {
          ...copia[copia.length - 1],
          content: "Ocurrió un error generando la respuesta. Intenta de nuevo.",
        };
        return copia;
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Card className="shadow-sm border-0 rounded-2xl">
      <CardContent className="p-0">
        <button
          onClick={() => setExpandido((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4"
        >
          <div className="flex items-center gap-2.5">
            <MessageCircle className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-semibold text-gray-700">
              Preguntar a la IA sobre este equipo
            </span>
          </div>
          <span className="text-xs text-gray-400">{expandido ? "Ocultar" : "Mostrar"}</span>
        </button>

        {expandido && (
          <div className="px-5 pb-5 space-y-3">
            <div className="max-h-80 overflow-y-auto space-y-3 border border-gray-100 rounded-xl p-3 bg-gray-50">
              {mensajes.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-6">
                  Pregunta sobre manuales, mantenimientos, observaciones o datos técnicos de este equipo.
                </p>
              )}
              {mensajes.map((m, i) => (
                <div
                  key={i}
                  className={`text-sm rounded-xl px-3 py-2 max-w-[90%] ${
                    m.role === "user"
                      ? "bg-blue-600 text-white ml-auto"
                      : "bg-white border border-gray-200 text-gray-800"
                  }`}
                >
                  <p className="whitespace-pre-wrap">{m.content || "…"}</p>
                  {m.citations?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {m.citations.map((c, ci) => (
                        <span
                          key={ci}
                          className="inline-flex items-center gap-1 text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5"
                        >
                          <FileText className="w-3 h-3" />
                          {c.sourceLabel}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <div className="flex items-center gap-2">
              <input
                value={pregunta}
                onChange={(e) => setPregunta(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && enviarPregunta()}
                placeholder="Escribe tu pregunta…"
                className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
                disabled={enviando}
              />
              <button
                onClick={enviarPregunta}
                disabled={enviando || !pregunta.trim()}
                className="p-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-xl transition-colors"
              >
                {enviando ? (
                  <LoaderPinwheel className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ChatPanel;
